import { CommonModule } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLuxonDateModule, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, Subject, takeUntil } from 'rxjs';
import { Attendee, Country, Event, Fee, InvoiceStatus, invoiceStatuses, KeyLabelPair, MY_DATE_FORMATS, PaymentMode, paymentModes, PromoCode, Registration } from '../../../domain/registrator';
import { CountryService } from '../../../services/country.service';
import { EventService } from '../../../services/event.service';
import { PromocodeService } from '../../../services/promocode.service';
import { MessageService } from '../../../services/message.service';

function calculateAmount(fee: Fee, registrationDate: Date, earlyBirdDeadline: string | undefined | null,
  promoCode: PromoCode | null): number {
  console.debug('calculateAmount', fee, registrationDate, earlyBirdDeadline, promoCode);
  let basePrice: number = 0;
  if (!earlyBirdDeadline) {
    console.log('no earlybird deadline, using general price');
    basePrice = fee.generalPrice;
  } else if (!fee.earlyBirdPrice) {
    console.log('no earlybird price, returning general price');
    basePrice = fee.generalPrice;
  } else {
    console.debug('registrationDate', registrationDate);
    console.debug('earlyBirdDeadline', earlyBirdDeadline);
    console.debug('fee', fee.target, fee.earlyBirdPrice, fee.generalPrice);
    let d1 = new Date(registrationDate);
    let d2 = new Date(earlyBirdDeadline);
    d1.setHours(0, 0, 0, 0);
    d2.setHours(0, 0, 0, 0);
    console.log('registrationDate', d1);
    console.log('earlyBirdDeadline', d2);
    basePrice = (d1 <= d2 ? fee.earlyBirdPrice : fee.generalPrice);
  }
  if (promoCode != null) {
    return basePrice * (100 - promoCode.discountValue) / 100;
  }
  return basePrice;
}

@Component({
  selector: 'app-registration',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatRadioModule,
    MatTableModule,
    MatCardModule,
    MatListModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatTabsModule,
    MatDatepickerModule,
    MatSortModule,
    MatDialogModule,
    MatLuxonDateModule
  ],
  providers: [
    provideLuxonDateAdapter(),
    {
      provide: MAT_DATE_FORMATS,
      useValue: MY_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'en-GB', // Or your desired locale
    },
  ],
  templateUrl: './registration-edit.component.html',
  styleUrl: './registration-edit.component.scss'
})
export class RegistrationEditComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly countryService = inject(CountryService);
  private readonly eventService = inject(EventService)
  private readonly messageService = inject(MessageService);
  private readonly promoCodeService = inject(PromocodeService);
  private readonly dialogRef = inject(MatDialogRef<RegistrationEditComponent>)
  public paymentOptions: KeyLabelPair<keyof typeof PaymentMode>[] = paymentModes;
  public invoiceStatusOptions: KeyLabelPair<keyof typeof InvoiceStatus>[] = invoiceStatuses;

  registrationForm!: FormGroup;
  fees: Fee[] = [];
  countries: Country[] = [];
  eventName = '';
  appliedRate: number = 0;
  shouldHighlightAppliedRate: boolean = false;
  event = signal<Event | null>(null);

  private destroy$ = new Subject<void>(); // Used for automatic unsubscription

  private promoCodeSubject: BehaviorSubject<PromoCode | null>;
  promoCode$: Observable<PromoCode | null>;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { eventName: string, registration: Registration | null }) {
    this.promoCodeSubject = new BehaviorSubject<PromoCode | null>(
      data.registration?.promoCode ?? null
    );
    this.promoCode$ = this.promoCodeSubject.asObservable();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.buildForm();
    this.patchForm(this.data.registration!);
    this.countryService.countries.pipe(takeUntil(this.destroy$)).subscribe({
      next: (countries) => {
        this.countries = countries;
      },
      error: (error) => {
        console.error('Error loading countries:', error);
        this.messageService.showMessage('Failed to load countries list.', true);
      }
    });
  }

  private buildForm(): void {
    this.registrationForm = this.fb.group({
      registrationDate: [{ value: this.getCurrentDate(), disabled: true }, Validators.required],
      organisation: ['', Validators.required],
      promocode: [{ value: '', disabled: true }, { updateOn: 'blur' }],
      paymentMode: [{ value: '', disabled: true }, Validators.required],
      // appliedRate: [{ value: this.appliedRate, disabled: true }],
      freeTickets: [{ value: '', disabled: true }],
      payingTickets: [{ value: '', disabled: true }],
      fullRateTickets: [{ value: '', disabled: true }],
      discountRateTickets: [{ value: '', disabled: true }],
      fullRate: [{ value: '', disabled: true }],
      discountRate: [{ value: '', disabled: true }],
      // appliedDiscount: [{ value: '', disabled: true }],
      // appliedDiscountType: [{ value: '', disabled: true }],
      totalAmount: [{ value: '', disabled: true }],
      totalTickets: [{ value: '', disabled: true }],
      fee: [{ value: null, disabled: true }, Validators.required],
      invoice: this.fb.group({
        invoiceNumber: ['', Validators.required],
        invoiceDate: [this.getCurrentDate(), Validators.required],
        invoiceStatus: [{ value: '', disabled: true }, Validators.required],
        customerName: ['', Validators.required],
        contactName: ['', Validators.required],
        contactEmail: ['', [Validators.required, Validators.email]],
        address1: ['', Validators.required],
        address2: [''],
        zip: ['', Validators.required],
        city: ['', Validators.required],
        country: [null, Validators.required],
        vatNumber: [''],
        poNumber: [''],
      }),
      attendees: this.fb.array([])
    });
  }


  private patchForm(registration: Registration): void {
    console.debug('patchform', registration);

    // Sort attendees to get the corresponding first, then the others, with each category sorted alphabetically
    // on the lastname (there should be just one corresponding)
    const attendees = [...registration.attendees]
      .sort((a, b) => a.corresponding === b.corresponding
        ? a.lastName.localeCompare(b.lastName)
        : a.corresponding ? -1 : 1
      )
      .map(a => this.createAttendeeFormGroup(a));
    this.attendeesFormArray.clear();
    for (const a of attendees) {
      this.attendeesFormArray.push(a);
    }

    const registrationToPatch = {
      // registrationDate: registration.date ? formatDate(registration.date, "yyyy-MM-dd", "en-BE") : this.getCurrentDate(),
      registrationDate: registration.date,
      organisation: registration.organisation,
      paymentMode: registration.paymentMode,
      promocode: (registration.promoCode ? registration.promoCode.code : ''),
      fee: registration.fee,
      // appliedRate: registration.appliedRate,
      freeTickets: registration.freeTickets,
      payingTickets: registration.payingTickets,
      fullRateTickets: registration.fullRateTickets,
      discountRateTickets: registration.discountRateTickets,
      fullRate: registration.fullRate,
      discountRate: registration.discountRate,
      appliedDiscount: registration.appliedDiscount,
      appliedDiscountType: (registration.appliedDiscountType ? (registration.appliedDiscountType === 'PERCENT' ? '%' : (registration.appliedDiscountType === 'FIXED_AMOUNT' ? 'EUR' : 'N/A')) : 'N/A'),
      totalAmount: registration.totalAmount,
      totalTickets: registration.totalTickets,
      invoice: {
        invoiceNumber: (registration.invoice ? registration.invoice.number : 'no invoice'),
        invoiceStatus: (registration.invoice ? registration.invoice.status : null),
        // invoiceDate: (registration.invoice ? formatDate(registration.invoice.date, "yyyy-MM-dd", "en-BE") : this.getCurrentDate()),
        invoiceDate: (registration.invoice ? registration.invoice.date : this.getCurrentDate()),
        customerName: registration.customer?.name,
        contactName: registration.customer?.contactName,
        contactEmail: registration.customer?.email,
        address1: registration.customer?.address1,
        address2: registration.customer?.address2,
        zip: registration.customer?.zip,
        city: registration.customer?.city,
        country: registration.customer?.country.code,
        vatNumber: registration.customer?.vatNumber,
        poNumber: registration.poNumber,
      },
    };
    this.registrationForm.patchValue(registrationToPatch);

    if (!this.data.registration!.invoice) {
      this.invoiceFormGroup.disable();
    }

    if (registration.customer?.country) {
      const selectedCountry = this.countries.find(c => c.code === registration.customer?.country.code);
      if (selectedCountry) {
        this.invoiceFormGroup.get('country')?.patchValue(selectedCountry.code);
      }
    }
    if (registration.fee) {
      const selectedFee = this.fees.find(f => f.id === registration.fee?.id);
      if (selectedFee) {
        this.registrationForm.get('fee')?.patchValue(selectedFee);
      }
    }
  }

  private getCurrentDate(): string {
    return DateTime.now().toISODate();
  }

  get invoiceFormGroup(): FormGroup {
    return this.registrationForm.get('invoice') as FormGroup;
  }

  get attendeesFormArray(): FormArray {
    return this.registrationForm.get('attendees') as FormArray;
  }

  createAttendeeFormGroup(a: Attendee): FormGroup {
    return this.fb.group({
      jobTitle: [a.jobTitle],
      firstName: [a.firstName, Validators.required],
      lastName: [a.lastName, Validators.required],
      email: [a.email, Validators.compose([Validators.required, Validators.email, Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)])],
      corresponding: [a.corresponding]
    });
  }

  // onRegistrationDateChange(event: MatDatepickerInputEvent<Date>): void {
  //   const selectedDate = event.value;
  //   console.debug('Registration Date Changed (via dateChange):', this.registrationForm, selectedDate);
  //   if (selectedDate) {
  //     let newAmount: number = calculateAmount(this.registrationForm.controls['fee'].value, selectedDate, this.event()?.earlyBirdDeadline, this.promoCodeSubject.getValue());
  //     let oldAmount: number = this.data.registration!.appliedRate;
  //     this.appliedRate = newAmount;
  //     this.registrationForm.controls['appliedRate'].patchValue(this.appliedRate);
  //     this.shouldHighlightAppliedRate = (newAmount != oldAmount);
  //     if (newAmount != oldAmount) {
  //       console.debug('fee changed from ', oldAmount, ' to ', newAmount);
  //     }
  //   } else {
  //     console.error('No date selected.');
  //   }
  // }

  // onFeeChange(event: MatSelectChange): void {
  //   const selectedFee: Fee | null = event.value;

  //   if (selectedFee) {
  //     let newAmount: number = calculateAmount(selectedFee, this.registrationForm.controls['registrationDate'].value, this.event()?.earlyBirdDeadline, this.promoCodeSubject.getValue());
  //     let oldAmount: number = this.data.registration!.appliedRate;
  //     this.appliedRate = newAmount;
  //     this.registrationForm.controls['appliedRate'].patchValue(this.appliedRate);
  //     this.shouldHighlightAppliedRate = (newAmount != oldAmount);
  //     if (newAmount != oldAmount) {
  //       console.debug('fee changed from ', oldAmount, ' to ', newAmount);
  //     }
  //   } else {
  //     console.error('No fee selected.');
  //   }
  // }

  onSave(): void {
    if (this.registrationForm.valid) {
      const formValue: Registration = this.registrationForm.getRawValue();
      // Close the dialog and pass the form data back to the component that opened it
      this.dialogRef.close(formValue);
    } else {
      this.markAllAsTouched(this.registrationForm);
      console.log('Form is invalid', this.registrationForm);
    }
  }

  onCancel(): void {
    this.dialogRef.close(); // Close the dialog without passing any data (or pass null/false)
  }

  private markAllAsTouched(formGroup: FormGroup | FormArray): void {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markAllAsTouched(control);
      } else {
        control.markAsTouched();
      }
    });
  }

  compareById(obj1: any, obj2: any): boolean {
    return obj1 && obj2 && obj1.id === obj2.id;
  }

  compareByCountryCode(o1: any, o2: any): boolean {
    console.log(o1.code, o2.code);
    return o1 && o2 && o1.code === o2.code;
  }
}
